import { stringify } from "querystring";

import { Dispatch, KeyboardEvent, SetStateAction } from "react";
import { ToggleButton } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";

import Link from "next/link";

import {
  Anchor,
  Icon,
  LineClamp,
  QuestionSVG,
  SearchSVG,
  type SvgComponentType,
} from "@newsela/angelou";
import { useRouter } from "next/router";

import { ISuggestion } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/SearchMenu.constants";
import {
  flexColumnCss,
  flexRowCss,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/SearchSuggestionsPopout/SearchSuggestionsPopout.styles";
import { getHighlightedSuggestionText } from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/SearchSuggestionsPopout/SearchSuggestionsPopout.utils";
import {
  essentialQuestionTextCss,
  footerSectionCss,
  searchSuggestionAnchorCss,
  searchSuggestionIconCss,
  suggestionsUnorderedListCss,
  toggleSwitchContainerCss,
} from "@/base/NewselaGlobalLayoutV2/NavigationBar/SearchMenu/SearchSuggestionsPopout/SuggestionsList/SuggestionsList.styles";
import { AgathaSchemaParamsEnum } from "@/search/NewSearchPageMVP/types/IParametersSchema";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import useGetUserProperties from "@/users/v3/useGetUserProperties";

export const SEARCH_WITH_NEEDLE_URL = "/search?needle=";

export const isQuestionSuggestion = (category: string) => category === "eq";

const ToggleWithTrackEvent = withTrackEvent(ToggleButton);

export const focusSuggestion = ({
  index,
  increment,
}: {
  index: number;
  increment: boolean;
}) => {
  const suggestionAnchors = document.querySelectorAll(
    ".search-suggestion-anchor",
  );
  if (increment) {
    (suggestionAnchors[index + 1] as HTMLAnchorElement)?.focus();
  } else {
    (suggestionAnchors[index - 1] as HTMLAnchorElement)?.focus();
  }
};

const SuggestionsList = ({
  inputValue,
  suggestions,
  setShowSuggestions,
  pressed,
  onToggle,
}: ISuggestionsList) => {
  const { push, query } = useRouter();

  const { canSeeAiFeatures } = useGetUserProperties();
  const [aiSmartSearchExperiment] =
    useGetUserExperimentSegment("ai_assisted_search");
  const isAiSmartSearchExperimentOn = aiSmartSearchExperiment === "on";
  const canSeeAiSmartSearch = canSeeAiFeatures && isAiSmartSearchExperimentOn;

  const onKeyDown = (
    event: KeyboardEvent,
    suggestion: string,
    index: number,
  ) => {
    if (event.key === "Escape") {
      setShowSuggestions(false);
    }
    if (event.key === "Enter") {
      setShowSuggestions(false);
      const searchParams = new URLSearchParams(stringify(query));
      searchParams.set(AgathaSchemaParamsEnum.needle, suggestion);
      void push({
        pathname: "/search",
        search: `?${searchParams.toString()}`,
      });
    }
    const isNotLastSuggestion = index < suggestions.length;
    if (event.key === "ArrowDown" && isNotLastSuggestion) {
      event.preventDefault();
      focusSuggestion({ index, increment: true });
    }
    const isNotFirstSuggestion = index >= 0;
    if (event.key === "ArrowUp" && isNotFirstSuggestion) {
      event.preventDefault();
      focusSuggestion({ index, increment: false });
    }
  };

  return (
    <ul css={suggestionsUnorderedListCss}>
      {suggestions.map((item, index) => {
        let suggestion: string;
        let category: string;

        if (typeof item === "string") {
          suggestion = item;
          category = "";
        } else {
          suggestion = item.suggestion;
          category = item.category;
        }

        const isQuestion = isQuestionSuggestion(category);
        return (
          <li key={index}>
            <Link
              passHref
              legacyBehavior
              prefetch={false}
              href={encodeURI(`${SEARCH_WITH_NEEDLE_URL}${suggestion}`)}
            >
              <Anchor
                onKeyDown={(event) => onKeyDown(event, suggestion, index)}
                onClick={() => setShowSuggestions((show) => !show)}
                __cssFor={searchSuggestionAnchorCss}
                __classNameFor={{ root: "search-suggestion-anchor" }}
              >
                {isQuestion ? (
                  <Icon
                    SvgComponent={QuestionSVG as SvgComponentType}
                    __cssFor={searchSuggestionIconCss}
                  />
                ) : (
                  <Icon
                    SvgComponent={SearchSVG as SvgComponentType}
                    __cssFor={searchSuggestionIconCss}
                  />
                )}

                <div css={flexColumnCss}>
                  <div css={flexRowCss}>
                    {isQuestion ? (
                      <div data-testid="question-text">
                        <LineClamp lines={2} text={suggestion} />
                      </div>
                    ) : (
                      <div data-testid="highlighted-text">
                        {getHighlightedSuggestionText(suggestion, inputValue)}
                      </div>
                    )}
                  </div>

                  {isQuestionSuggestion(category) && (
                    <div css={essentialQuestionTextCss}>Essential question</div>
                  )}
                </div>
              </Anchor>
            </Link>
          </li>
        );
      })}
      {canSeeAiSmartSearch && (
        <div css={footerSectionCss}>
          <ToggleWithTrackEvent
            label="Enable Search with Luna"
            __cssFor={{
              switchContainerStyles: pressed ? toggleSwitchContainerCss : "",
            }}
            dataProps={{
              "data-qa-selector": "enable-search-with-luna-toggle",
            }}
            objectType={objectTypes.user}
            actionName={actionNames.interact}
            streamProperties={{
              description: "Toggle Search with Luna",
            }}
            pressed={pressed}
            onClick={onToggle}
          />
        </div>
      )}
    </ul>
  );
};

interface ISuggestionsList {
  inputValue: string;
  suggestions: ISuggestion[] | string[];
  setShowSuggestions: Dispatch<SetStateAction<boolean>>;
  onToggle: () => void;
  pressed: boolean;
}

SuggestionsList.displayName = "SuggestionsList";
export { SuggestionsList };
